import { z } from 'zod';

/**
 * Import der Altdaten aus der Base44-PM-App (ADR 2026-08-24-base44-altdaten-import).
 *
 * Ergänzt bestehende ProERP-Projekte um historische Stammdaten/Phasen und
 * importiert Aufgaben, Checklisten-Punkte, QM-Prozesse, Montage-Einsätze und
 * Urlaubs-/Krankheits-/Arztanträge aus sieben CSV-Exporten. Zwei Schritte,
 * dieselbe Eingabe: `analyze` (Trockenlauf, schreibt nichts) und `execute`
 * (schreibt idempotent — ein zweiter Lauf mit denselben CSVs legt nichts
 * doppelt an). Der Client liest die Dateien und schickt den Text — alle
 * sieben CSVs sind optional, mindestens eine muss mitgeschickt werden
 * (Rückmeldung 2026-08-25: `projectCsv` war ursprünglich Pflicht, obwohl
 * Checklisten-Vorlage, QM-Prozesse und Urlaubs-/Krankheits-/Arztanträge
 * ohnehin keinen Projektbezug haben — ein reiner Urlaubsantrags-Import
 * scheiterte deshalb unnötig an einer fehlenden Projekt-CSV). `projectCsv`
 * ist nur die Join-Basis für Aufgaben/Checklisten-Punkte/Montage-Einsätze —
 * fehlt sie, werden diese drei Entitäten ohne Projektzuordnung importiert
 * (`skippedNoProject`), fehlt eine andere CSV, wird die betroffene Entität
 * ganz übersprungen.
 */
export const base44ImportInputSchema = z
  .object({
    projectCsv: z.string().min(1).max(20_000_000).optional(),
    taskCsv: z.string().max(20_000_000).optional(),
    checklistCsv: z.string().max(20_000_000).optional(),
    checklistTemplateCsv: z.string().max(20_000_000).optional(),
    qmCsv: z.string().max(20_000_000).optional(),
    assemblyCsv: z.string().max(20_000_000).optional(),
    leaveRequestCsv: z.string().max(20_000_000).optional(),
    /**
     * Bereits importierte Aufgaben/Checklisten-Punkte/QM-Prozesse/Montage-
     * Einsätze/Urlaubs-Anträge mit dem aktuellen CSV-Inhalt überschreiben statt
     * überspringen (z. B. wenn Kürzel/Status in Base44 nach dem ersten Import
     * nachgepflegt wurden). Betrifft NICHT die Projekt-Anreicherung und
     * Projekt-Phasen — die bleiben additiv (§ ADR Punkt 3/8).
     */
    updateExisting: z.boolean().optional().default(false),
  })
  .refine(
    (input) =>
      Boolean(
        input.projectCsv ||
          input.taskCsv ||
          input.checklistCsv ||
          input.checklistTemplateCsv ||
          input.qmCsv ||
          input.assemblyCsv ||
          input.leaveRequestCsv,
      ),
    { message: 'Mindestens eine CSV-Datei muss angehängt werden.' },
  );
export type Base44ImportInput = z.infer<typeof base44ImportInputSchema>;

export const base44ImportEntityKeySchema = z.enum([
  'project',
  'projectPhases',
  'task',
  'checklist',
  'checklistTemplate',
  'qm',
  'assembly',
  'leaveRequest',
]);
export type Base44ImportEntityKey = z.infer<typeof base44ImportEntityKeySchema>;

const base44ImportFailureSchema = z.object({
  id: z.string(),
  reason: z.string(),
});

export const base44ImportEntityReportSchema = z.object({
  entity: base44ImportEntityKeySchema,
  label: z.string(),
  read: z.number().int(),
  /** Neu angelegt (bzw. bei `project`: angereichert). */
  created: z.number().int(),
  /** Deterministische ID existiert schon und `updateExisting` war gesetzt — überschrieben. */
  updated: z.number().int(),
  /** Deterministische ID existiert schon, `updateExisting` war nicht gesetzt — Re-Run, No-op. */
  alreadyImported: z.number().int(),
  /** Nur bei `project`: Anzahl vorher leerer Felder, die gesetzt wurden/würden. */
  filledFields: z.number().int(),
  /** Zeile referenziert ein altes Projekt ohne Treffer in ProERP. */
  skippedNoProject: z.number().int(),
  /** Bewusst ausgeschlossen (z. B. Montage-Kategorie „Arzt"). */
  skippedFiltered: z.number().int(),
  failed: z.array(base44ImportFailureSchema),
});
export type Base44ImportEntityReport = z.infer<typeof base44ImportEntityReportSchema>;

/** Ähnlichkeits-Vorschlag für eine nicht gefundene Projektnummer — rein informativ. */
export const base44ImportSuggestionSchema = z.object({
  legacyCode: z.string(),
  candidates: z.array(
    z.object({
      code: z.string(),
      name: z.string(),
      score: z.number(),
    }),
  ),
});
export type Base44ImportSuggestion = z.infer<typeof base44ImportSuggestionSchema>;

export const base44ImportReportSchema = z.object({
  /** true = `execute` (geschrieben), false = `analyze` (Trockenlauf, nichts geschrieben). */
  applied: z.boolean(),
  /**
   * Projektnummern über den Ziffernblock am Ende gefunden — ProERP-Codes sind
   * reine Nummern, Base44 stellt ein Typ_Jahr_-Präfix voran (z. B.
   * "D_26_52072" -> "52072").
   */
  digitMatches: z.number().int(),
  /** Projektnummern, nur durch Normalisierung (Trennzeichen/Groß-Kleinschreibung) gefunden. */
  normalizedMatches: z.number().int(),
  /** Projektnummern aus dem Export ohne Treffer in ProERP (keine Zeile wird dafür angelegt). */
  notFoundCodes: z.array(z.string()),
  suggestions: z.array(base44ImportSuggestionSchema),
  entities: z.array(base44ImportEntityReportSchema),
});
export type Base44ImportReport = z.infer<typeof base44ImportReportSchema>;
